import { useStaticQuery, graphql } from 'gatsby'
import { useMemo } from 'react'

import { Program, CircleOffer } from '@src/types'
import { LocaleSlug } from '@src/types/common'
import { Nodes } from '@src/types/graphql'

type PartialProgram = Pick<Program, 'slug' | 'safireId'>

type MapResult =
  | {
      type: 'program' | 'microprogram' | 'masterClass'
      slug: LocaleSlug
    }
  | {
      type: 'circle'
    }

type NavigationProgramsTabResult = {
  programs: Nodes<PartialProgram>
  microprograms: Nodes<PartialProgram>
  masterClasses: Nodes<PartialProgram>
  circles: Nodes<Pick<CircleOffer, 'safireId'>>
}

export default function useSafireIdMap() {
  const { programs, microprograms, masterClasses: masterclasses, circles } = useStaticQuery<
    NavigationProgramsTabResult
  >(
    graphql`
      {
        programs: allSanityProgram(
          filter: { active: { eq: true }, programType: { eq: "program" } }
        ) {
          nodes {
            slug {
              fr {
                current
              }
              en {
                current
              }
            }
            safireId
          }
        }
        microprograms: allSanityProgram(
          filter: { active: { eq: true }, programType: { eq: "microprogram" } }
        ) {
          nodes {
            slug {
              fr {
                current
              }
              en {
                current
              }
            }
            safireId
          }
        }
        masterClasses: allSanityProgram(
          filter: { active: { eq: true }, programType: { eq: "masterClass" } }
        ) {
          nodes {
            slug {
              fr {
                current
              }
              en {
                current
              }
            }
            safireId
          }
        }
        circles: allSanityCircle(filter: { active: { eq: true } }) {
          nodes {
            safireId
          }
        }
      }
    `
  )

  const safireIdMap = useMemo(() => {
    const safireIdMap = new Map<number, MapResult>()
    for (const program of programs.nodes) {
      const safireId = program.safireId
      if (safireId) {
        safireIdMap.set(safireId, {
          type: 'program',
          slug: program.slug,
        })
      }
    }

    for (const microprogram of microprograms.nodes) {
      const safireId = microprogram.safireId
      if (safireId) {
        safireIdMap.set(safireId, {
          type: 'microprogram',
          slug: microprogram.slug,
        })
      }
    }

    for (const masterclass of masterclasses.nodes) {
      const safireId = masterclass.safireId
      if (safireId) {
        safireIdMap.set(safireId, {
          type: 'masterClass',
          slug: masterclass.slug,
        })
      }
    }

    for (const circle of circles.nodes) {
      const safireId = circle.safireId
      if (safireId) {
        safireIdMap.set(safireId, {
          type: 'circle',
        })
      }
    }

    return safireIdMap
  }, [programs, microprograms, masterclasses, circles])

  return safireIdMap
}
